import React, { useState, useEffect, useCallback } from 'react';
import { projectsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableHead, 
    TableHeader, 
    TableRow 
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import {
    Plus,
    Search,
    Filter,
    MoreHorizontal,
    Pencil,
    Trash2,
    Loader2,
    CalendarIcon,
    X,
    FileSpreadsheet,
    Eye
} from 'lucide-react';

const emptyProject = {
    cliente: '',
    nome_progetto: '',
    tipologia_progetto: '',
    citta: '',
    architetto: '',
    data_prevista_consegna: null,
    quantita: '',
    serie: '',
    colore: '',
    formato: '',
    finitura: '',
    prezzo_confermato: '',
    note: ''
};

export default function DashboardPage() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        cliente: '',
        citta: '',
        architetto: '',
        tipologia_progetto: '',
        date_from: '',
        date_to: ''
    });
    const [showFilters, setShowFilters] = useState(false);
    
    // Dialog states
    const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [viewingProject, setViewingProject] = useState(null);
    const [editingProject, setEditingProject] = useState(null);
    const [formData, setFormData] = useState(emptyProject);
    const [saving, setSaving] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        try {
            const params = { ...filters, search: searchTerm };
            Object.keys(params).forEach(key => {
                if (!params[key]) delete params[key];
            });
            const response = await projectsApi.getAll(params);
            setProjects(response.data);
        } catch (error) {
            toast.error('Errore nel caricamento dei progetti');
        } finally {
            setLoading(false);
        }
    }, [filters, searchTerm]);

    useEffect(() => {
        const debounce = setTimeout(fetchProjects, 300);
        return () => clearTimeout(debounce);
    }, [fetchProjects]);

    const handleExportExcel = async () => {
        try {
            const params = { ...filters, search: searchTerm };
            Object.keys(params).forEach(key => {
                if (!params[key]) delete params[key];
            });
            
            const response = await projectsApi.exportExcel(params);
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `progetti_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Export completato');
        } catch (error) {
            toast.error('Errore durante l\'export');
        }
    };

    // Open view dialog (read-only)
    const handleViewProject = (project) => {
        setViewingProject(project);
        setIsViewDialogOpen(true);
    };

    // Switch from view to edit mode
    const handleSwitchToEdit = () => {
        setIsViewDialogOpen(false);
        handleOpenFormDialog(viewingProject);
    };

    // Open form dialog for create/edit
    const handleOpenFormDialog = (project = null) => {
        if (project) {
            setEditingProject(project);
            setFormData({
                cliente: project.cliente || '',
                nome_progetto: project.nome_progetto || '',
                tipologia_progetto: project.tipologia_progetto || '',
                citta: project.citta || '',
                architetto: project.architetto || '',
                data_prevista_consegna: project.data_prevista_consegna || null,
                quantita: project.quantita || '',
                serie: project.serie || '',
                colore: project.colore || '',
                formato: project.formato || '',
                finitura: project.finitura || '',
                prezzo_confermato: project.prezzo_confermato || '',
                note: project.note || ''
            });
        } else {
            setEditingProject(null);
            setFormData(emptyProject);
        }
        setIsFormDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.cliente || !formData.nome_progetto) {
            toast.error('Cliente e Nome Progetto sono obbligatori');
            return;
        }

        setSaving(true);
        try {
            if (editingProject) {
                await projectsApi.update(editingProject.id, formData);
                toast.success('Progetto aggiornato');
            } else {
                await projectsApi.create(formData);
                toast.success('Progetto creato');
            }
            setIsFormDialogOpen(false);
            fetchProjects();
        } catch (error) {
            toast.error('Errore durante il salvataggio');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await projectsApi.delete(id);
            toast.success('Progetto eliminato');
            setDeleteConfirmId(null);
            setIsViewDialogOpen(false);
            fetchProjects();
        } catch (error) {
            toast.error('Errore durante l\'eliminazione');
        }
    };

    const clearFilters = () => {
        setFilters({
            cliente: '',
            citta: '',
            architetto: '',
            tipologia_progetto: '',
            date_from: '',
            date_to: ''
        });
        setSearchTerm('');
    };

    const hasActiveFilters = Object.values(filters).some(v => v) || searchTerm;

    // Field display component for view mode
    const FieldDisplay = ({ label, value }) => (
        <div className="space-y-1">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</p>
            <p className="text-sm text-slate-900">{value || '-'}</p>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        Progetti
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {projects.length} progett{projects.length === 1 ? 'o' : 'i'} totali
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant="outline"
                        onClick={handleExportExcel}
                        className="rounded-sm"
                        data-testid="export-excel-btn"
                    >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        Esporta Excel
                    </Button>
                    <Button
                        onClick={() => handleOpenFormDialog()}
                        className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                        data-testid="add-project-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nuovo Progetto
                    </Button>
                </div>
            </div>

            {/* Search & Filters */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            placeholder="Cerca progetti..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 rounded-sm"
                            data-testid="search-input"
                        />
                    </div>
                    <Button
                        variant="outline"
                        onClick={() => setShowFilters(!showFilters)}
                        className={`rounded-sm ${showFilters ? 'bg-slate-100' : ''}`}
                        data-testid="toggle-filters-btn"
                    >
                        <Filter className="w-4 h-4 mr-2" />
                        Filtri
                        {hasActiveFilters && (
                            <span className="ml-2 w-2 h-2 bg-blue-600 rounded-full" />
                        )}
                    </Button>
                    {hasActiveFilters && (
                        <Button
                            variant="ghost"
                            onClick={clearFilters}
                            className="rounded-sm text-slate-500"
                            data-testid="clear-filters-btn"
                        >
                            <X className="w-4 h-4 mr-2" />
                            Cancella
                        </Button>
                    )}
                </div>

                {showFilters && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-slate-100 animate-slide-up">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Cliente
                            </Label>
                            <Input
                                value={filters.cliente}
                                onChange={(e) => setFilters({ ...filters, cliente: e.target.value })}
                                placeholder="Filtra per cliente"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Città
                            </Label>
                            <Input
                                value={filters.citta}
                                onChange={(e) => setFilters({ ...filters, citta: e.target.value })}
                                placeholder="Filtra per città"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Architetto
                            </Label>
                            <Input
                                value={filters.architetto}
                                onChange={(e) => setFilters({ ...filters, architetto: e.target.value })}
                                placeholder="Filtra per architetto"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Tipologia
                            </Label>
                            <Input
                                value={filters.tipologia_progetto}
                                onChange={(e) => setFilters({ ...filters, tipologia_progetto: e.target.value })}
                                placeholder="Filtra per tipologia"
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Da Data
                            </Label>
                            <Input
                                type="date"
                                value={filters.date_from}
                                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                                className="rounded-sm"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                A Data
                            </Label>
                            <Input
                                type="date"
                                value={filters.date_to}
                                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                                className="rounded-sm"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Projects Table */}
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                ) : projects.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div 
                            className="w-32 h-32 rounded-sm mb-4 bg-cover bg-center opacity-50"
                            style={{
                                backgroundImage: `url('https://images.unsplash.com/photo-1770231908157-aad637739f03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3R1cmFsJTIwbWF0ZXJpYWxzJTIwdGV4dHVyZXMlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc3MDI4MjM2NXww&ixlib=rb-4.1.0&q=85')`
                            }}
                        />
                        <h3 className="text-lg font-semibold text-slate-700">Nessun progetto trovato</h3>
                        <p className="text-sm text-slate-500 mt-1">
                            {hasActiveFilters ? 'Prova a modificare i filtri' : 'Inizia aggiungendo il tuo primo progetto'}
                        </p>
                        {!hasActiveFilters && (
                            <Button
                                onClick={() => handleOpenFormDialog()}
                                className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Aggiungi Progetto
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table className="data-table">
                            <TableHeader>
                                <TableRow className="bg-slate-50 border-b border-slate-200">
                                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 h-10">Data</TableHead>
                                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 h-10">Cliente</TableHead>
                                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 h-10">Progetto</TableHead>
                                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 h-10 hidden md:table-cell">Tipologia</TableHead>
                                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 h-10 hidden lg:table-cell">Città</TableHead>
                                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 h-10 hidden xl:table-cell">Architetto</TableHead>
                                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 h-10 hidden xl:table-cell">Consegna</TableHead>
                                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 h-10 w-12"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {projects.map((project) => (
                                    <TableRow 
                                        key={project.id} 
                                        className="hover:bg-slate-50/50 transition-colors border-b border-slate-100 last:border-0 cursor-pointer"
                                        onClick={() => handleViewProject(project)}
                                        data-testid={`project-row-${project.id}`}
                                    >
                                        <TableCell className="p-4 font-mono text-sm text-slate-600">
                                            {project.data_registrazione}
                                        </TableCell>
                                        <TableCell className="p-4 font-medium text-slate-900">
                                            {project.cliente}
                                        </TableCell>
                                        <TableCell className="p-4 text-slate-700">
                                            {project.nome_progetto}
                                        </TableCell>
                                        <TableCell className="p-4 text-slate-600 hidden md:table-cell">
                                            {project.tipologia_progetto || '-'}
                                        </TableCell>
                                        <TableCell className="p-4 text-slate-600 hidden lg:table-cell">
                                            {project.citta || '-'}
                                        </TableCell>
                                        <TableCell className="p-4 text-slate-600 hidden xl:table-cell">
                                            {project.architetto || '-'}
                                        </TableCell>
                                        <TableCell className="p-4 font-mono text-sm text-slate-600 hidden xl:table-cell">
                                            {project.data_prevista_consegna || '-'}
                                        </TableCell>
                                        <TableCell className="p-4" onClick={(e) => e.stopPropagation()}>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        data-testid={`project-menu-${project.id}`}
                                                    >
                                                        <MoreHorizontal className="w-4 h-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => handleViewProject(project)}>
                                                        <Eye className="w-4 h-4 mr-2" />
                                                        Visualizza
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => handleOpenFormDialog(project)}>
                                                        <Pencil className="w-4 h-4 mr-2" />
                                                        Modifica
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem 
                                                        onClick={() => setDeleteConfirmId(project.id)}
                                                        className="text-red-600"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Elimina
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* View Project Dialog (Read-Only) */}
            <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                            {viewingProject?.nome_progetto}
                        </DialogTitle>
                        <DialogDescription>
                            Cliente: {viewingProject?.cliente}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {viewingProject && (
                        <div className="py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Info principali */}
                                <div className="md:col-span-3 grid grid-cols-2 md:grid-cols-4 gap-4 pb-4 border-b border-slate-200">
                                    <FieldDisplay label="Data Registrazione" value={viewingProject.data_registrazione} />
                                    <FieldDisplay label="Data Consegna" value={viewingProject.data_prevista_consegna} />
                                    <FieldDisplay label="Tipologia" value={viewingProject.tipologia_progetto} />
                                    <FieldDisplay label="Città" value={viewingProject.citta} />
                                </div>
                                
                                {/* Dettagli progetto */}
                                <FieldDisplay label="Architetto" value={viewingProject.architetto} />
                                <FieldDisplay label="Quantità" value={viewingProject.quantita} />
                                <FieldDisplay label="Prezzo Confermato" value={viewingProject.prezzo_confermato} />
                                
                                {/* Specifiche materiale */}
                                <FieldDisplay label="Serie" value={viewingProject.serie} />
                                <FieldDisplay label="Colore" value={viewingProject.colore} />
                                <FieldDisplay label="Formato" value={viewingProject.formato} />
                                <FieldDisplay label="Finitura" value={viewingProject.finitura} />
                                
                                {/* Note */}
                                <div className="md:col-span-3 pt-4 border-t border-slate-200">
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Note</p>
                                    <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 p-3 rounded-sm min-h-[60px]">
                                        {viewingProject.note || 'Nessuna nota'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirmId(viewingProject?.id)}
                            className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina
                        </Button>
                        <Button
                            onClick={handleSwitchToEdit}
                            className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                            data-testid="edit-from-view-btn"
                        >
                            <Pencil className="w-4 h-4 mr-2" />
                            Modifica
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit/Create Project Dialog */}
            <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                            {editingProject ? 'Modifica Progetto' : 'Nuovo Progetto'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingProject ? 'Modifica i dettagli del progetto' : 'Inserisci i dettagli del nuovo progetto'}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSave}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Cliente *
                                </Label>
                                <Input
                                    value={formData.cliente}
                                    onChange={(e) => setFormData({ ...formData, cliente: e.target.value })}
                                    placeholder="Nome cliente"
                                    className="rounded-sm"
                                    data-testid="form-cliente"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Nome Progetto *
                                </Label>
                                <Input
                                    value={formData.nome_progetto}
                                    onChange={(e) => setFormData({ ...formData, nome_progetto: e.target.value })}
                                    placeholder="Nome del progetto"
                                    className="rounded-sm"
                                    data-testid="form-nome-progetto"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Tipologia
                                </Label>
                                <Input
                                    value={formData.tipologia_progetto}
                                    onChange={(e) => setFormData({ ...formData, tipologia_progetto: e.target.value })}
                                    placeholder="Es: Residenziale, Commerciale"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Città
                                </Label>
                                <Input
                                    value={formData.citta}
                                    onChange={(e) => setFormData({ ...formData, citta: e.target.value })}
                                    placeholder="Città del progetto"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Architetto
                                </Label>
                                <Input
                                    value={formData.architetto}
                                    onChange={(e) => setFormData({ ...formData, architetto: e.target.value })}
                                    placeholder="Nome architetto"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Data Prevista Consegna
                                </Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-full justify-start text-left font-normal rounded-sm"
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {formData.data_prevista_consegna 
                                                ? format(new Date(formData.data_prevista_consegna), 'dd/MM/yyyy', { locale: it })
                                                : 'Seleziona data'
                                            }
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={formData.data_prevista_consegna ? new Date(formData.data_prevista_consegna) : undefined}
                                            onSelect={(date) => setFormData({ 
                                                ...formData, 
                                                data_prevista_consegna: date ? format(date, 'yyyy-MM-dd') : null 
                                            })}
                                            locale={it}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Quantità
                                </Label>
                                <Input
                                    value={formData.quantita}
                                    onChange={(e) => setFormData({ ...formData, quantita: e.target.value })}
                                    placeholder="Es: 100 mq"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Serie
                                </Label>
                                <Input
                                    value={formData.serie}
                                    onChange={(e) => setFormData({ ...formData, serie: e.target.value })}
                                    placeholder="Serie prodotto"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Colore
                                </Label>
                                <Input
                                    value={formData.colore}
                                    onChange={(e) => setFormData({ ...formData, colore: e.target.value })}
                                    placeholder="Colore"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Formato
                                </Label>
                                <Input
                                    value={formData.formato}
                                    onChange={(e) => setFormData({ ...formData, formato: e.target.value })}
                                    placeholder="Es: 60x120"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Finitura
                                </Label>
                                <Input
                                    value={formData.finitura}
                                    onChange={(e) => setFormData({ ...formData, finitura: e.target.value })}
                                    placeholder="Es: Opaco, Lucido"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Prezzo Confermato
                                </Label>
                                <Input
                                    value={formData.prezzo_confermato}
                                    onChange={(e) => setFormData({ ...formData, prezzo_confermato: e.target.value })}
                                    placeholder="Es: €25/mq"
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                    Note
                                </Label>
                                <Textarea
                                    value={formData.note}
                                    onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                    placeholder="Note aggiuntive..."
                                    className="rounded-sm min-h-[100px]"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsFormDialogOpen(false)}
                                className="rounded-sm"
                            >
                                Annulla
                            </Button>
                            <Button
                                type="submit"
                                className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                                disabled={saving}
                                data-testid="form-save-btn"
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Salvataggio...
                                    </>
                                ) : (
                                    'Salva'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Conferma eliminazione</DialogTitle>
                        <DialogDescription>
                            Sei sicuro di voler eliminare questo progetto? L'azione non può essere annullata.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirmId(null)}
                            className="rounded-sm"
                        >
                            Annulla
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => handleDelete(deleteConfirmId)}
                            className="rounded-sm"
                            data-testid="confirm-delete-btn"
                        >
                            Elimina
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
